<script lang="ts" setup>
// Properties
import LabelTag from "~/components/form/LabelTag.vue";
// Data
import { ref } from "vue";
import InputTag from "~/components/form/InputTag.vue";
import CheckboxTag from "~/components/form/CheckboxTag.vue";
import ButtonPrimary from "~/components/ButtonPrimary.vue";
import ButtonSecondary from "~/components/ButtonSecondary.vue";
import axios from "axios";
import { useRoute } from "#app";
import { useUserStore } from "~/store/user";

const props = defineProps({
    redirectUrl: {
        type: String,
        default: "",
    },
});

const email = ref("");
const password = ref("");
const { login, oauthLogin, errorMsg, processing } = useUserAuth();

const config = useRuntimeConfig();
const route = useRoute();

const oauthProvider = ref(route.query.oauthProvider as string);
const pid = ref(route.query.pid as string);

onMounted(async () => {
    // Since oauth is not fully handle on the backend, due to CORS issue, we'll need to log in user after a
    // successful callback.
    if (oauthProvider.value && pid.value) {
        await oauthLogin(oauthProvider.value, pid.value, useUserStore().loginRedirect);
    }
});

async function oauthSignIn(provider: string) {
    const { data } = await axios.get(config.public.STATAMIC_HOST + `/oauth/${provider}/redirect-frontend`);
    window.location.href = data.redirectURL;
}
</script>

<template>
    <div class="relative px-8 py-8">
        <div
            v-if="oauthProvider && pid"
            class="absolute -top-12 left-0 z-40 mt-12 flex h-full w-full justify-center bg-gray-300/20 text-center"
        >
            <div class="place-self-center">
                <img src="/images/spinnersBlocksWave.svg" alt="Spinner" class="w-20" />
                Processing...
            </div>
        </div>
        <form @submit.prevent="login(email, password, redirectUrl)">
            <div>
                <LabelTag for="email">Email Address:</LabelTag>
                <InputTag
                    id="email"
                    v-model="email"
                    name="email"
                    type="email"
                    autofocus="autofocus"
                    autocomplete="on"
                    class="w-full"
                    placeholder="Email Address"
                    required
                />
            </div>
            <div class="relative mt-4 mb-2">
                <LabelTag for="password" class="label-style">Password:</LabelTag>
                <InputTag
                    id="password"
                    v-model="password"
                    type="password"
                    class="input-style w-full"
                    placeholder="Password"
                    required
                />
                <NuxtLink to="/forgot-password" class="text-abc-blue absolute top-0 right-0">Forgot Password?</NuxtLink>
            </div>
            <div class="mt-4 mb-2 flex">
                <CheckboxTag class="flex-1">Remember Me</CheckboxTag>
                <NuxtLink to="/create-account" class="text-abc-blue text-right">Create Account</NuxtLink>
            </div>
            <hr class="my-5" />

            <div v-show="errorMsg" class="mt-4 text-center text-red-700">{{ errorMsg }}</div>

            <ButtonPrimary type="submit" class="relative mt-2 w-full" :disabled="processing">
                <img src="/images/fa/lock-keyhole-solid.svg" class="absolute top-3 left-6 w-4" />
                Sign In
            </ButtonPrimary>

            <ButtonSecondary
                type="button"
                class="relative mt-3 w-full"
                :disabled="processing"
                @click="oauthSignIn('google')"
            >
                <img src="/images/icons/google.svg" class="absolute top-3 left-6 w-4" alt="Google" />
                Google Sign-In
            </ButtonSecondary>
            <ButtonSecondary
                type="button"
                class="relative mt-3 w-full"
                :disabled="processing"
                @click="oauthSignIn('microsoft')"
            >
                <img src="/images/icons/microsoft.svg" class="absolute top-3 left-6 w-4" alt="Microsoft" />
                Microsoft Sign-In
            </ButtonSecondary>
        </form>
    </div>
</template>

<style scoped></style>
